import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crosshair, CircleNotch, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function NameStrike() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [scan, setScan] = useState(false);
  const [f, setF] = useState({ email: "", contact: "", budget: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setSent(false);
    if (q.trim().length < 2) { setRes(null); setScan(false); return undefined; }
    setScan(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q: q.trim(), limit: 6 } });
        setRes(data);
      } catch { setRes([]); } finally { setScan(false); }
    }, 550);
    return () => clearTimeout(t);
  }, [q]);

  const exact = res?.find((p) => p.title.toLowerCase() === q.trim().toLowerCase());

  const request = async (e) => {
    e.preventDefault();
    try {
      await api.post("/requests", { wanted: q.trim(), email: f.email, contact: f.contact, budget: parseFloat(f.budget) || 0, kind: "rsn" });
      setSent(true);
      toast.success("Strike request sent. We will hunt that name down.");
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <section id="strike" className="relative max-w-[1500px] mx-auto px-5 md:px-10 py-24" data-testid="name-strike-section">
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
        <div>
          <div className="overline-label mb-4">// name strike radar</div>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">Type a name.<br /><span className="hot-text">We lock on.</span></h2>
          <p className="text-[#aaa] mt-6 max-w-md">Scan our stock live. No match? Put a bounty on any name and we go hunting for it.</p>
          <div className="relative mt-8">
            <Crosshair size={22} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#ff2a55]" />
            <input value={q} onChange={(e) => setQ(e.target.value.slice(0, 20))} placeholder="Type your dream RSN..." className="field !pl-12 !py-4 text-lg font-bold" data-testid="name-strike-input" />
          </div>
          <div className="mt-6 min-h-[130px]" data-testid="name-strike-status">
            {scan && <div className="flex items-center gap-3 text-[#ff2a55] text-xs tracking-[0.25em] uppercase"><CircleNotch size={22} className="animate-spin" /> Sweeping radar...</div>}
            {!scan && res && exact && <div className="flex items-center gap-3 text-[#35d07f] text-xs tracking-[0.25em] uppercase" data-testid="name-strike-exact"><CheckCircle size={22} weight="fill" /> Target locked. Exact match in stock.</div>}
            {!scan && res && !exact && res.length > 0 && <div className="text-[#ffb020] text-xs tracking-[0.25em] uppercase" data-testid="name-strike-similar">No exact match. {res.length} similar target{res.length > 1 ? "s" : ""} found.</div>}
            {!scan && res && res.length === 0 && (
              sent ? <div className="text-[#35d07f] text-sm" data-testid="name-strike-sent">Bounty placed. Watch your email and Discord.</div> :
              <form onSubmit={request} className="space-y-3" data-testid="name-strike-form">
                <div className="text-[#ff2a55] text-xs tracking-[0.25em] uppercase">Nothing on radar. Place a bounty:</div>
                <div className="grid grid-cols-2 gap-3">
                  <input required type="email" className="field" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="strike-email-input" />
                  <input required className="field" placeholder="Discord" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} data-testid="strike-contact-input" />
                </div>
                <div className="flex gap-3">
                  <input type="number" min="0" className="field" placeholder="Budget (M GP)" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} data-testid="strike-budget-input" />
                  <button className="btn-bomb whitespace-nowrap" data-testid="strike-submit-button">Place bounty</button>
                </div>
              </form>
            )}
          </div>
        </div>
        <div className="relative">
          <div className="relative aspect-[16/10] border border-[#ff2a55]/40 bg-[#080808] overflow-hidden scanlines flex items-center justify-center" data-testid="name-strike-preview">
            <img src="/brand/art_explosion.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(rgba(255,42,85,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,42,85,0.15) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
            <div className="absolute w-[80%] aspect-square rounded-full border border-[#ff2a55]/30" />
            <div className="absolute w-[50%] aspect-square rounded-full border border-[#ff2a55]/30" />
            <motion.div className="absolute w-[80%] aspect-square rounded-full" style={{ background: "conic-gradient(from 0deg, rgba(255,42,85,0.5), transparent 25%)" }} animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 3.2, ease: "linear" }} />
            <AnimatePresence mode="wait">
              <motion.div key={q || "empty"} initial={{ scale: 1.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}
                className="relative z-10 font-display chrome-text text-center px-4 break-all" style={{ fontSize: `clamp(2.2rem, ${q.length > 10 ? 6 : 9}vw, 7rem)`, lineHeight: 1 }}>
                {q || "YOUR NAME"}
              </motion.div>
            </AnimatePresence>
          </div>
          {res && res.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="name-strike-results">
              {res.slice(0, 4).map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
